<script setup>
import { computed } from "vue";
import AnimatedArrow from "@components/AnimatedArrow.vue";

const props = defineProps({
  variant: { type: String, default: "primary" },
  href: { type: String, default: undefined },
  label: { type: String, default: undefined },
  type: { type: String, default: "button" },
  square: { type: Boolean, default: false },
  borderColor: { type: String, default: "accent" },
});

const tag = computed(() => (props.href ? "a" : "button"));
</script>

<template>
  <component
    :is="tag"
    :href="href"
    :type="!href ? type : undefined"
    class="group relative flex h-[48px] items-center justify-center overflow-clip rounded-[8px]"
    :class="[
      square
        ? 'w-[48px] bg-primary p-[12px] transition-transform duration-150 active:scale-95'
        : [
            variant !== 'ghost' && 'gap-[12px] py-[16px] pl-[16px] pr-[12px]',
            {
              'bg-primary': variant === 'primary',
              'bg-black': variant === 'dark',
              'border-2 border-black/10 p-[16px]': variant === 'ghost',
            },
          ],
    ]"
  >
    <template v-if="square">
      <span class="relative z-10">
        <slot />
      </span>
    </template>

    <template v-else>
      <span
        class="relative z-10 shrink-0 font-obviously font-bold text-[14px] leading-none tracking-[-0.14px]"
        :class="{
          'text-black': variant === 'primary' || variant === 'ghost',
          'text-primary': variant === 'dark',
        }"
      >
        <slot>{{ label }}</slot>
      </span>

      <AnimatedArrow
        v-if="variant !== 'ghost'"
        :size="28"
        :inverted="variant === 'primary'"
        class="relative z-10"
      />
    </template>

    <template v-if="variant !== 'ghost'">
      <span
        class="btn-corner-bl pointer-events-none absolute border-solid rounded-bl-[8px]"
        :class="{
          'border-accent': borderColor === 'accent',
          'border-primary': borderColor === 'primary',
        }"
      />
      <span class="btn-corner-tr pointer-events-none absolute">
        <span
          class="btn-corner-tr-inner block border-white border-solid rounded-bl-[8px] size-full"
        />
      </span>
    </template>
  </component>
</template>

<style scoped>
.btn-corner-bl {
  inset: 33.33% 21% 0 0;
  border-bottom-width: 3px;
  border-left-width: 3px;
  transition:
    inset 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    border-width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.group:hover .btn-corner-bl {
  inset: 25% 10.6% 0 0;
  border-bottom-width: 4px;
  border-left-width: 4px;
}

.btn-corner-tr {
  inset: 0 0 33.33% 21%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    inset 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    border-width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.group:hover .btn-corner-tr {
  inset: 0 0 25% 10.6%;
}

.btn-corner-tr-inner {
  rotate: 180deg;
  border-bottom-width: 3px;
  border-left-width: 3px;
  transition: border-width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.group:hover .btn-corner-tr-inner {
  border-bottom-width: 2px;
  border-left-width: 2px;
}
</style>
